import { requestUsersLocation } from "../utils/request-users-location.js";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "@myphx-engineering/phx-design-system/hooks";

//#region src/hooks/use-location-autocomplete.ts
const searchDebounce = 400;
/**
* Debounced address search, error handling, and "current location" resolution shared by every
* location-autocomplete UI in this package. Originally inline in LocationPicker — extracted so
* a sibling component could reuse the exact same search/geocode behavior instead of a second,
* driftable copy.
*/
const useLocationAutocomplete = ({ geolocation, onLocationChange, locationService, requireFullAddress, setError, autofill = true }) => {
	const { t } = useTranslation("loc");
	const genericError = t("locationPicker.errors.generic");
	const notFound = t("locationPicker.errors.notFound");
	const currentLocationLabel = `${t("locationPicker.currentLocation")}`;
	const [textValue, setTextValue] = useState(geolocation?.name);
	const [options, setOptions] = useState([]);
	const [errorMessage, setErrorMessage] = useState();
	const [isMyLocationLoading, setIsMyLocationLoading] = useState(false);
	const autoFillLocation = useCallback(async () => {
		setIsMyLocationLoading(true);
		const location = await requestUsersLocation(locationService);
		if (location) {
			setTextValue(location.name);
			onLocationChange(location);
		}
		setIsMyLocationLoading(false);
	}, [locationService, onLocationChange]);
	useEffect(() => {
		if (autofill && !geolocation && !isMyLocationLoading) autoFillLocation();
	}, [
		autofill,
		autoFillLocation,
		geolocation,
		isMyLocationLoading
	]);
	const getOptionsForAddress = async (address) => {
		const { locations, error } = await locationService.getLocationsByAddress(address, requireFullAddress);
		if (locations?.length) return locations;
		setErrorMessage(error ? genericError : notFound);
		setError?.(!!error);
		return [];
	};
	const search = useDebouncedCallback(async (input) => {
		setOptions([...await getOptionsForAddress(input)]);
	}, searchDebounce);
	const handleInputChange = (text) => {
		setErrorMessage(void 0);
		setError?.(false);
		if (text) search(text);
		else onLocationChange(void 0);
		setTextValue(text);
	};
	const handleBlur = () => {
		if (!options.find((x) => x.name === textValue)) {
			setErrorMessage(notFound);
			setError?.(true);
		}
	};
	const dataItems = [{
		label: geolocation?.name ?? "",
		value: currentLocationLabel
	}, ...options.filter((option) => option.name !== geolocation?.name).map((option) => ({
		label: option.name,
		value: option.name
	}))];
	const handleOptionSubmit = (value) => {
		const location = options.find((x) => x.name === value);
		if (value === currentLocationLabel) autoFillLocation();
		else if (location) {
			setTextValue(location.name);
			onLocationChange(location);
		}
	};
	return {
		textValue,
		dataItems,
		options,
		errorMessage,
		isMyLocationLoading,
		currentLocationLabel,
		autoFillLocation,
		handleInputChange,
		handleBlur,
		handleOptionSubmit
	};
};

//#endregion
export { useLocationAutocomplete };